import { css } from '@emotion/react';

import EFinderImage, {
  EFinderImageSizes,
} from '@components/common/EFinderImage';
import SvgIcon from '@components/common/SvgIcon';

import ProfileImg from '@public/icons/profile_32.svg';

import { MarketItemType } from '@constants';
import { IMarketItemUserPictureResource, ISimpleUser } from '@model';
import { useUserMarketItemInventory } from '@utils/hook';
import { CSSProps, OnClickProps, PartialProperty } from '@utils/misc';

type AvatarSizeMetric = { size: number; radius: number };

export const AVATAR_SIZE_METRICS: AvatarSizeMetric[] = [
  {
    size: 16,
    radius: 5,
  },
  {
    size: 24,
    radius: 8,
  },
  {
    size: 32,
    radius: 12,
  },
  {
    size: 44,
    radius: 16,
  },
  {
    size: 50,
    radius: 10,
  },
  {
    size: 64,
    radius: 24,
  },
  {
    size: 100,
    radius: 36,
  },
  {
    size: 116,
    radius: 15,
  },
  {
    size: 189,
    radius: 20,
  },
];

export const enum AvatarSize {
  xsmall,
  small,
  medium,
  large,
  largeSquare,
  xlarge,
  xxlarge,
  xxxlarge,
  xxxxlarge,
}

const avatarWrapCSS = ({ size }: AvatarSizeMetric) => css`
  position: relative;
  width: ${size}px;
  height: ${size}px;
`;

const avatarCSS = ({ size, radius }: AvatarSizeMetric) => css`
  position: relative;
  width: ${size}px;
  height: ${size}px;
  border-radius: ${radius}px;
  overflow: hidden;
`;

const avatarDecorationCSS = ({ size }: AvatarSizeMetric) => {
  const ratio = size > 32 ? 2 : 1.75;
  const width = size * ratio;
  const axis = (width - size) / 2;

  return css`
    position: absolute;
    top: -${axis}px;
    left: -${axis}px;
    width: ${width}px;
    height: ${width}px;
  `;
};

type Props = {
  size?: AvatarSize;
  decoration?: IMarketItemUserPictureResource;
  hideDecoration?: boolean;
} & PartialProperty<Pick<ISimpleUser, 'id' | 'picture'>, 'id'>;

const Avatar = ({
  className,
  id,
  size = AvatarSize.medium,
  picture,
  decoration: overrideDecoration,
  hideDecoration = false,
  onClick,
}: Props & CSSProps & OnClickProps) => {
  const avatarMetrics = AVATAR_SIZE_METRICS[size];

  const { data, getDecoration } = useUserMarketItemInventory(id);
  const decoration = !hideDecoration
    ? overrideDecoration ?? getDecoration(data, MarketItemType.userPicture)
    : undefined;

  return (
    <div
      css={avatarWrapCSS(avatarMetrics)}
      className={className}
      onClick={onClick}
    >
      <div css={avatarCSS(avatarMetrics)}>
        {picture && picture.key ? (
          <EFinderImage
            image={picture}
            h={EFinderImageSizes.chatbot}
            radius={avatarMetrics.radius}
          />
        ) : (
          <SvgIcon icon={ProfileImg} d={avatarMetrics.size} />
        )}
      </div>
      {decoration && (
        <div css={avatarDecorationCSS(avatarMetrics)}>
          <EFinderImage
            image={
              size >= AvatarSize.large
                ? decoration.object.pageProfile
                : decoration.object.pageComment
            }
          />
        </div>
      )}
    </div>
  );
};

export default Avatar;
